import { useEffect, useState, useRef } from 'react';
import FeeBreakdownControls from './FeeBreakdownControls';
import encompassFields from '../../data/feebreakdown_form.json';
import { fieldReader, fieldWriter } from '../../services/loanApi';
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from '../../utils/loanEditLock';
import { getAccessToken } from "../../services/authApi";
import Toast from '../shared/Toast';
import Modal from '../shared/Modal';
import LoadingOverlay from '../shared/LoadingOverlay';
import { error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import "react-datepicker/dist/react-datepicker.css";
import { generateFeeBreakdownPdf } from "../../services/internalApi";

const btnClass = (disabled) =>
    `btn-base btn-save ${disabled ? "cursor-not-allowed opacity-60" : ""}`;

function ActionButtons({ isReadOnly, onSave, onPrint }) {
    return (
        <div className="flex gap-2">
            <button onClick={onPrint} className="btn-base btn-save">
                Print
            </button>
            <button onClick={() => onSave(false)} className={btnClass(isReadOnly)} disabled={isReadOnly}>
                Save
            </button>
        </div>
    );
}


export default function FeeBreakdown() {
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [loanid, setLoanid] = useState('');
    const [loanNumber, setLoanNumber] = useState('');

    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState('Loan saved successfully...');
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [skipFetch, setSkipFetch] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");
    const [initialSubmitDate, setInitialSubmitDate] = useState('');
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const retryRef = useRef(false);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    loanObject.all().then(async function (loan) {
                        setLoanid(loan.id);
                        setLoanNumber(loan.loanNumber);
                        const tpo = loan?.tpo || {};
                        const submittedDate = tpo.initialSubmitDate || '';
                        setInitialSubmitDate(submittedDate);

                        await getToken(false);
                    });
                });
            } catch (error) {
                const errorCode = logError("COMMERCIAL_APP_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "CashToClose",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                console.error("Auth block Error:", error);
                setLoading(false);
            }
        } else {
            console.warn("elli.script.guest is not available.");
            setLoading(false);
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
            setSkipFetch(skipFieldLoad);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };

    const fetchFields = async () => {
        const fieldIds = [...new Set([
            ...encompassFields.fields.map(field => field.id)
        ])];

        setLoading(true);
        let locked = false;
        try {
            const data = await fieldReader(token, [...fieldIds, ...LOAN_EDIT_LOCK_FIELD_IDS], loanid);
            setFieldValues(data);
            locked = isLoanEditLocked(data);
            setEditLocked(locked);
        } catch (err) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
            console.error("Field fetch failed", err);
        } finally {
            if ((initialSubmitDate && initialSubmitDate !== "//") || locked) {
                // Delay to ensure DOM is rendered
                setTimeout(() => {
                    makeFormReadOnly();
                }, 100);
            }
            setLoading(false);
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            'input:not([type=hidden]):not([readonly]):not([data-force-edit])',
            'textarea:not([readonly]):not([data-force-edit])',
            'select:not([disabled]):not([data-force-edit])',
            'input[type=checkbox]:not([disabled]):not([data-force-edit])'
        ];

        const elements = document.querySelectorAll(selectors.join(', '));

        elements.forEach(el => {
            if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
                el.setAttribute('readOnly', true);
            } else {
                el.setAttribute('disabled', true);
            }

            el.classList.add('cursor-not-allowed');
        });
    };

    useEffect(() => {
        if (!token) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }
        fetchFields();
    }, [token, loanid]);

    useEffect(() => {
        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            const hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");

                const vanishTimer = setTimeout(() => {
                    hideToast();
                }, 500);

                return () => clearTimeout(vanishTimer);
            }, 4000);

            return () => clearTimeout(hideTimer);
        }
    }, [showToast]);

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const isReadOnly = (initialSubmitDate && initialSubmitDate !== "//") || editLocked;

    // Utility to read UI value for a field id (mirrors earlier logic)
    const getUiFieldValue = (rawId, fieldMeta) => {
        const escapedId = rawId.replace(/\./g, '\\.');
        const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
        const noCheckbox = document.getElementById(`field-${rawId}-no`);

        if (yesCheckbox && noCheckbox) {
            if (fieldMeta?.datatype === "YN") {
                return yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
            }
            return yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
        }

        const groupChecks = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
        if (groupChecks && groupChecks.length > 1) {
            const checked = [...groupChecks].find(cb => cb.checked);
            return checked?.value || "";
        }

        const input = document.getElementById(`field-${rawId}`);
        if (!input) return "";

        if (input.type === 'checkbox') {
            if (fieldMeta?.datatype === "YN") return input.checked ? "true" : "false";
            if (fieldMeta?.datatype === "Y") return input.checked ? "true" : "";
            return input.checked ? (input.value || "true") : "";
        }

        if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
            return input.options[input.selectedIndex]?.value || "";
        }

        let val = input.value || "";
        if (fieldMeta?.datatype === "money" && typeof val === "string") {
            val = val.replace(/,/g, "");
        }
        return val;
    };

    const validateRequiredFields = () => {
        const required = encompassFields.fields.filter(f => f.required);
        const missing = required.filter(field => {
            const el = document.getElementById(`field-${field.id}`);
            const value = el?.value?.trim?.() ?? "";
            // also check yes/no pair
            const yes = document.getElementById(`field-${field.id}-yes`);
            const no = document.getElementById(`field-${field.id}-no`);
            if ((yes && no) && (!yes.checked && !no.checked)) return true;
            return !value;
        });

        return missing;
    };

    const handleSubmit = async (isRetry = false) => {
        setLoadingText('Saving...');
        setLoading(true);
        setIsSaving(true);
        let updatedFields = [];
        try {
            // basic required check
            const missing = validateRequiredFields();
            if (missing.length > 0) {
                const messages = missing.map(f => `• ${f.name}`);
                setWarningToastMessage(`Please fill all required fields:<br/> ${messages.join('<br/> ')}`);
                setWarningToastVisible(true);
                setTimeout(() => setWarningToastVisible(false), 5000);
                return;
            }

            // Build updatedFields payload
            const fieldsToWrite = encompassFields.fields.filter(f => f.isUpdate !== false);

            const snapshot = {};

            updatedFields = fieldsToWrite.map(f => {
                const rawId = f.id;
                const escapedId = rawId.replace(/\./g, '\\.');
                const fieldMeta = f;
                const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
                const noCheckbox = document.getElementById(`field-${rawId}-no`);

                let value = "";

                if (yesCheckbox && noCheckbox) {
                    if (fieldMeta?.datatype === "YN") {
                        value = yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
                    } else {
                        value = yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
                    }
                } else {
                    // checkbox group?
                    const checkboxes = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
                    if (checkboxes && checkboxes.length > 1) {
                        const checked = [...checkboxes].find(cb => cb.checked);
                        value = checked?.value || fieldValues[rawId] || "";
                    } else {
                        const input = document.getElementById(`field-${rawId}`);
                        if (input) {
                            if (input.type === 'checkbox') {
                                if (fieldMeta?.datatype === "YN") {
                                    value = input.checked ? "true" : "false";
                                } else if (fieldMeta?.datatype === "Y") {
                                    value = input.checked ? "true" : "";
                                } else {
                                    value = input.checked ? (input.value || "true") : "";
                                }
                            } else if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
                                value = input.options[input.selectedIndex]?.value || "";
                            } else {
                                value = input.value || "";
                                if (fieldMeta?.datatype === "money" && typeof value === "string") {
                                    value = value.replace(/,/g, "");
                                }
                            }
                        } else {
                            value = fieldValues[rawId] || "";
                        }
                    }
                }

                if (fieldMeta?.datatype === "decimal") {
                    if (typeof value === "string") {
                        value = value.replace(/[$,]/g, "").trim();
                    }
                }
                snapshot[f.id] = value;

                return {
                    id: rawId,
                    value
                };
            });
            setFieldValues(prev => ({ ...prev, ...snapshot }));

            // send to API
            await fieldWriter(token, loanid, updatedFields);

            // success
            setShowToastText('Loan saved successfully...');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 10000);
            // refresh fields after save
            fetchFields();
            retryRef.current = false;
        } catch (error) {
            const errorCode = logError("COMMERCIAL_APP_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "CashToClose",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            // If 401 try refresh token once
            if (error.response?.status === 401 && !isRetry) {
                console.warn('401 detected, regenerating token...');
                await getToken(true);
                retryRef.current = true;
                return handleSubmit(true);
            }

            const json = error.response?.data || {};
            const title = (json.summary === "Conflict" && json.details?.includes("locked by another user"))
                ? "Loan Locked by Another User"
                : (json.summary === "Bad Request" && Array.isArray(json.errors) && json.errors.length > 0)
                    ? "Invalid Fields Detected"
                    : "Submission Error";

            setErrorTitle(title);

            const mainDetail = json.details || "Submission failed.";
            // map field errors if present
            let fieldErrText = "";
            if (Array.isArray(json.errors) && json.errors.length > 0) {
                fieldErrText = json.errors.map(err => {
                    const match = err.summary?.match?.(/changes\[(\d+)\]\.value/);
                    if (match) {
                        const idx = Number(match[1]);
                        const pf = updatedFields[idx];
                        const fm = encompassFields.fields.find(ff => ff.id === pf?.id);
                        const name = fm?.name || pf?.id;
                        return `${name} (${pf?.id}): ${err.details}`;
                    }
                    return `${err.summary}\n${err.details}`;
                }).join('\n');
            }

            setErrorMessage(`${mainDetail}${fieldErrText ? "\n" + fieldErrText : ""}`);
            setShowErrorToast(true);
        } finally {
            setLoading(false);
            setIsSaving(false);
        }
    };

    // optional Next stub - you can wire this if needed
    const handleNext = async () => {
        // placeholder if you want Next navigation
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            setLoadingText("Generating PDF...");

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            // 1️⃣ Collect all field names and values
            const payloadFields = encompassFields.fields.map(f => ({
                name: f.name,
                value: fieldValues[f.id] ?? ""
            }));

            // 2️⃣ Call API via service helper
            const pdfBlob = await generateFeeBreakdownPdf(authCode, loanid, loanNumber, payloadFields);

            // 3️⃣ Trigger download
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(pdfBlob);
            link.download = "FeeBreakdown.pdf";
            link.click();

            setShowToastText("Fee Breakdown PDF generated successfully!");
            setShowToast(true);
        } catch (err) {
            console.error("Fee Breakdown PDF generation failed:", err);
            setErrorMessage("Failed to generate Fee Breakdown PDF.");
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <LoadingOverlay loading={loading || isSaving} text={loadingText || 'Saving...'} />

            <Toast
                visible={toastVisible}
                type="success"
                message={showToastText}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showErrorToast}
                onClose={() => setShowErrorToast(false)}
                type="error"
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Fee Break-down</h2>

                    <div className="flex justify-end mt-4">
                        <ActionButtons
                            isReadOnly={isReadOnly}
                            showNext={false}
                            onSave={handleSubmit}
                            onPrint={handlePrint}
                            onNext={handleNext}
                            containerClass=""
                        />
                    </div>

                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-grow">
                            <FeeBreakdownControls
                                Fields={encompassFields.fields}
                                fieldValues={fieldValues}
                            />
                        </div>
                    </div>

                    {/* bottom save as well */}
                    <div className="flex justify-end mt-4">
                        <ActionButtons
                            isReadOnly={isReadOnly}
                            showNext={false}
                            onSave={handleSubmit}
                            onNext={handleNext}
                            onPrint={handlePrint}
                            containerClass=""
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
